import { put, select, call } from 'redux-saga/effects'
import { get, pick } from 'lodash/fp'
import _ from 'lodash'
import UUID from 'uuid/v1'
import { toast } from 'react-toastify'
import axios from 'axios'

import {
  FORM_SET_STAGE_START,
  FORM_SET_STAGE_SUCCEEDED,
  FORM_SET_STAGE_ERROR,
  FORM_SET_STAGE_FINALLY,
  FINALIZE_FORM_START,
  FINALIZE_FORM_ERROR,
  FINALIZE_FORM_SUCCEEDED,
  FINALIZE_FORM_FINALLY,
  FORM_SET_QUESTIONS_VALUE,
  FORM_DETAIL_START,
  FORM_DETAIL_SUCCEEDED,
  FORM_DETAIL_ERROR,
  FORM_DETAIL_FINALLY,
  FORM_SET_PENDING_SUCCEEDED,
  FORM_SELECT_POINT_START,
  FORM_SELECT_POINT_ERROR,
  FORM_SELECT_POINT_SUCCEEDED,
} from '../../actions'
import { PUT_EXTRAS } from '../../Controllers/Extras/extras.actions'

export const getState = get('Forms')
export const getStatePendingForm = pick(['Forms', 'Points'])
export const getUUID = () => new Promise(resolve => resolve(UUID()))

const validateQuestions = (questions, valuesKeyedByQuestions) => {
  const result = _.reduce(questions, (acum, question) => {
    if (!question.required) return acum

    const aux = valuesKeyedByQuestions[question._id]

    if (aux && (aux.value !== null || aux.value !== undefined || String(aux.value).length)) {
      return acum
    }

    return {
      incomplete: acum.incomplete.concat(question),
      valuesWithError: {
        ...acum.valuesWithError,
        [question._id]: {
          ...acum.valuesWithError[question._id],
          error: true,
        },
      },
    }
  }, { incomplete: [], valuesWithError: valuesKeyedByQuestions })

  return {
    isValid: !result.incomplete.length,
    valuesWithError: result.valuesWithError,
  }
}

export default {
  * changeStage({ payload }) {
    try {
      yield put(FORM_SET_STAGE_START())
      const {
        nextStage,
        prevStage,
        stageSelected,
      } = payload
      const {
        questionsValue,
      } = yield select(getState)

      if (prevStage) return yield put(FORM_SET_STAGE_SUCCEEDED(prevStage))

      const questions = _.flattenDeep(
        stageSelected.questionsGroups
          .map(questionGroup => questionGroup.questions),
      )

      const {
        isValid,
        valuesWithError,
      } = validateQuestions(questions, questionsValue)

      if (!isValid) {
        toast.warn('Debe completar los campos, para continuar')

        return yield put(FORM_SET_QUESTIONS_VALUE(valuesWithError))
      }

      return yield put(FORM_SET_STAGE_SUCCEEDED(nextStage))
    } catch (error) {
      return yield put(FORM_SET_STAGE_ERROR(`Ha ocurrido un error inesperado, ${error}`))
    } finally {
      yield put(FORM_SET_STAGE_FINALLY())
    }
  },
  finalize: history => function* finalize({ payload }) {
    try {
      yield put(FINALIZE_FORM_START())
      const {
        questionsGroups: lastQuestionsGroups,
        stage_id: payloadStageId,
        form,
        time,
      } = payload
      const {
        formSelected,
        pointSelected,
        stageSelected,
        questionsValue,
        startFormDate,
        formPointPending,
      } = yield select(getState)

      const questions = _.flattenDeep(
        lastQuestionsGroups.map(questionGroup => questionGroup.questions),
      )

      const {
        isValid,
        valuesWithError,
      } = validateQuestions(questions, questionsValue)

      if (!isValid) {
        toast.warn('Debe completar los campos, para finalizar')

        return yield put(FORM_SET_QUESTIONS_VALUE(valuesWithError))
      }

      const stageId = form.type !== 'flow'
        ? stageSelected
        : payloadStageId

      const uuid = yield call(getUUID)

      const formPoint = formPointPending
        ? {
          ...formPointPending,
          stage_id: stageId,
          pending: false,
          finalize: true,
          origin: 'web',
        }
        : {
          created_at: new Date(startFormDate).toJSON(),
          form_id: formSelected.value,
          point_id: pointSelected.value,
          apartment_id: null,
          stage_id: stageId,
          pending: false,
          finalize: true,
          client_uuid: uuid,
          origin: 'web',
          time,
        }

      const { data: formPointResponse } = formPointPending
        ? yield call(axios.put, `api/forms_points/${formPointPending._id}`, formPoint)
        : yield call(axios.post, 'api/forms_points', formPoint)

      const formPointId = formPointPending
        ? formPointPending._id
        : formPointResponse._id

      const submissions = Object.keys(questionsValue).map(key => ({
        question_id: key,
        value: questionsValue[key].value,
        form_point_id: formPointId,
      }))
      const { data: submissionsResponse } = yield call(axios.post, 'api/submissions', submissions)
      const submissionsWithIds = submissionsResponse.map((id, index) => ({
        ...id,
        ...submissions[index],
      }))

      toast.success('Formulario finalizado exitosamente')
      history.replace(`/dashboard/forms/new/detail/${formPointId}`)

      return yield put(FINALIZE_FORM_SUCCEEDED({
        submissions: submissionsWithIds,
        formsPoints: {
          ...formPoint,
          ...(!formPointPending ? formPointResponse : {}),
        },
      }))
    } catch (error) {
      return yield put(FINALIZE_FORM_ERROR(`Ha ocurrido un error inesperado, ${error}`))
    } finally {
      yield put(FINALIZE_FORM_FINALLY())
    }
  },
  getDetail: history => function* getDetail({ payload: { id, url } }) {
    try {
      yield put(FORM_DETAIL_START())

      history.push(url)

      const {
        submissions,
      } = yield select(getState)

      const submissionsGrouped = _.groupBy(submissions, 'form_point_id')
      const submissionsById = submissionsGrouped[id] || []

      if (submissionsById.length) {
        return yield put(FORM_DETAIL_SUCCEEDED([]))
      }

      const { data: submissionsResponse } = yield call(axios.get, 'api/submissions/user', {
        params: {
          q: { form_point_id: id },
        },
      })

      return yield put(FORM_DETAIL_SUCCEEDED(submissionsResponse))
    } catch (error) {
      return yield put(FORM_DETAIL_ERROR(error))
    } finally {
      yield put(FORM_DETAIL_FINALLY())
    }
  },
  continuePendingForm: history => function* continuePendingForm({ payload: formPoint }) {
    const {
      Forms: { forms },
      Points: { data: points },
    } = yield select(getStatePendingForm)
    const formsKeyed = _.keyBy(forms, '_id')
    const pointsKeyed = _.keyBy(points, '_id')
    const form = formsKeyed[formPoint.form_id]
    const point = pointsKeyed[formPoint.point_id]
    const preparePayload = {
      formSelected: {
        label: form.name,
        value: form._id,
      },
      pointSelected: {
        label: point.name,
        value: point._id,
      },
      stageSelected: formPoint.stage_id,
      startFormDate: new Date(formPoint.created_at),
      formPointPending: formPoint,
    }
    yield put(FORM_SET_PENDING_SUCCEEDED(preparePayload))
    history.push('/dashboard/forms/new')
  },
  * onSelectPoint({ payload }) {
    try {
      yield put(FORM_SELECT_POINT_START())
      const { data } = yield select(get('Extras'))

      if (data[payload.value]) {
        return yield put(FORM_SELECT_POINT_SUCCEEDED({
          point: payload,
        }))
      }

      const { data: extras } = yield call(axios.get, 'api/extras', {
        params: {
          q: {
            source: 'Point',
            source_id: payload.value,
            valid: true,
            visible: true,
          },
        },
      })

      yield put(PUT_EXTRAS({ [payload.value]: extras }))

      return yield put(FORM_SELECT_POINT_SUCCEEDED({
        point: payload,
      }))
    } catch (error) {
      return yield put(FORM_SELECT_POINT_ERROR(error))
    }
  },
}
